import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from "react-router-dom";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import { supabase } from "@/integrations/supabase/client";
import AuthLayout from "@/components/auth/AuthLayout";
import AuthInput from "@/components/auth/AuthInput";
import { trackFormSubmitted, trackInvitationAccepted } from "@/utils/amplitudeEvents";

const Auth: React.FC = () => {
  const { signIn, user, loading } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [companyInfo, setCompanyInfo] = useState<{ id: string; name: string } | null>(null);

  // Get invitation params from URL
  const companySlug = searchParams.get('company');
  const invitationId = searchParams.get('invitation');

  // Track invitation link access
  useEffect(() => {
    if (companySlug) {
      trackInvitationAccepted({
        company_slug: companySlug,
        invitation_id: invitationId || undefined,
        source_page: 'login',
      });
    }
  }, [companySlug, invitationId]);

  // Fetch company info if invitation params present
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!companySlug) return;

      // Use secure function for public company lookup
      const { data, error } = await supabase
        .rpc('get_public_company_info', { company_slug: companySlug });

      if (!error && data && data.length > 0) {
        setCompanyInfo({ id: data[0].id, name: data[0].name });
      }
    };

    fetchCompanyInfo();
  }, [companySlug]);

  // Handle authenticated user with invitation
  const handleInvitationForUser = async (userId: string) => {
    if (!companyInfo || !companySlug) return;

    try {
      await supabase.rpc('associate_user_with_company', {
        p_user_id: userId,
        p_company_id: companyInfo.id
      });

      if (invitationId) {
        await supabase
          .from('company_invitations')
          .update({ used_at: new Date().toISOString() })
          .eq('id', invitationId)
          .is('used_at', null);
      }

      toast({
        title: "Welcome!",
        description: `You've joined ${companyInfo.name}.`
      });
    } catch (error) {
      console.error('Error processing invitation:', error);
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    const handleRedirect = async () => {
      if (user && !loading) {
        if (companyInfo) {
          await handleInvitationForUser(user.id);
        }
        navigate("/dashboard");
      }
    };

    handleRedirect();
  }, [user, loading, navigate, companyInfo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const { error } = await signIn(formData.email, formData.password);
    
    if (error) {
      trackFormSubmitted({ form_name: 'sign_in', success: false, error_message: error.message });
      toast({
        title: "Unable to sign in",
        description: error.message === "Invalid login credentials" 
          ? "The email or password you entered is incorrect. Please try again."
          : error.message,
        variant: "destructive"
      });
    } else {
      trackFormSubmitted({ form_name: 'sign_in', success: true });
    }
    
    setIsSubmitting(false);
  };

  return (
    <>
      <AuthLayout 
        title="Welcome back" 
        subtitle="Sign in to access your dashboard and team insights"
      >
        <form onSubmit={handleSignIn} className="space-y-5">
          <AuthInput
            id="signin-email"
            name="email"
            type="email"
            label={t('auth.email')}
            placeholder="you@company.com"
            value={formData.email}
            onChange={handleInputChange}
            required
            icon="email"
            autoComplete="email"
          />
          
          <AuthInput
            id="signin-password"
            name="password"
            type="password"
            label={t('auth.password')}
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleInputChange}
            required
            icon="password"
            showPasswordToggle
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            autoComplete="current-password"
          />
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 text-base font-medium"
            disabled={isSubmitting || loading}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                {t('auth.signIn')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">New to Zentrix?</span>
          </div>
        </div>

        {/* Sign Up CTA */}
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">
            Start your free 7-day trial and transform your hiring process
          </p>
          <Button 
            variant="outline"
            onClick={() => navigate('/signup')}
            className="w-full h-12 text-base font-medium border-2 hover:bg-muted"
          >
            Create free account
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </AuthLayout>
      
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </>
  );
};

export default Auth;
